import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Save, MapPin } from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { cn } from '../lib/utils';

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: { 
    opacity: 0,
    transition: { delay: 0.1, duration: 0.2 }
  }
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      duration: 0.3, 
      ease: [0.16, 1, 0.3, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: 20, 
    scale: 0.98,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

const EditVariantNameModal = ({ isOpen, onClose, onSave, variant }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [variantName, setVariantName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset form and focus input when modal opens
  useEffect(() => {
    if (isOpen && variant) {
      setVariantName(variant.name || '');
      setValidationError('');
      // Focus input after a short delay to let the modal animation complete
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select(); // Select all text for easy editing
      }, 100);
    }
  }, [isOpen, variant]);

  // Handle input change
  const handleChange = (e) => {
    setVariantName(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate
    if (!variantName.trim()) {
      setValidationError('Variant name is required');
      return;
    }

    if (variantName.trim() === variant?.name) {
      // No changes made
      onClose();
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('brain_groupings')
        .update({ name: variantName.trim() })
        .eq('id', variant.id);

      if (error) throw error;

      // Call onSave callback if provided
      if (onSave) {
        onSave({ ...variant, name: variantName.trim() });
      }

      toast.success('Variant name updated');
      onClose();
    } catch (error) {
      console.error('Error updating variant name:', error);
      toast.error('Failed to update variant name');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if not open
  if (!isOpen || !variant) return null;

  return createPortal(
    <motion.div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10002] flex items-center justify-center"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-2 sm:mx-0 overflow-hidden"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <MapPin className="h-4 w-4 text-blue-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Edit Variant Name
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="space-y-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="variantName" className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                Variant Name <span className="text-red-500 ml-0.5">*</span>
              </Label>
              <Input
                id="variantName"
                ref={inputRef}
                value={variantName}
                onChange={handleChange}
                placeholder="Enter variant name"
                className={cn(
                  "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                  validationError && "border-red-300 focus:border-red-500 focus:ring-red-500"
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                  if (e.key === 'Escape') {
                    onClose();
                  }
                }}
              />
              {validationError && (
                <motion.p 
                  className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  {validationError}
                </motion.p>
              )}
            </div>

            {/* Helper text */}
            <p className="text-xs text-gray-500">
              This name will be displayed in the sidebar and helps identify different versions of this setting.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 py-3 sm:py-4 px-4 sm:px-6 bg-gray-50 flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !variantName.trim() || variantName.trim() === variant?.name}
            size="sm"
            className="bg-gray-900 hover:bg-gray-800 text-white min-w-[100px]"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-1">
                <svg className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xs sm:text-sm">Saving</span>
              </span>
            ) : (
              <span className="text-xs sm:text-sm">Save Changes</span>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default EditVariantNameModal;
